import {
  assetGroupsQueryToDto,
  mapAssetGroupsResponseDto,
  type AssetGroupsQuery,
  type AssetGroupsResult,
  type AssetGroupsResponseDto,
  mapAccountCapabilityDto,
  type AccountCapability,
  type AccountCapabilityDto,
  accountPatchRequestToDto,
  bulkUnlinkRequestToDto,
  linkAccountsRequestToDto,
  mapAccountDetailResponseDto,
  mapAccountsListResponseDto,
  mapBulkUnlinkResponseDto,
  createAssetImportsRequestToDto,
  mapCancelSyncResponseDto,
  mapGaUploadEntityDto,
  mapCreateMetaAssetUploadsResponseDto,
  mapMetaAssetUploadDto,
  type CreateMetaAssetUploadsRequest,
  type CreateMetaAssetUploadsResponse,
  type CreateMetaAssetUploadsResponseDto,
  type GetMetaAssetUploadResponseDto,
  type MetaAssetUpload,
  mapAdAssetImportAcceptedDto,
  mapAdAccountAssetsResponseDto,
  mapAdAssetImportJobDto,
  adAssetImportRequestToDto,
  mapGroupAeiReportDto,
  mapDiscoveryAccountsResultDto,
  mapLinkAccountsResponseDto,
  mapOAuthIdentitiesResultDto,
  mapOAuthLinkResultDto,
  oauthLinkRequestToDto,
  mapPostImportsResponseDto,
  mapReportQueryResponseDto,
  mapSyncJobDetailResponseDto,
  mapSyncJobsListResponseDto,
  mapSyncSubmitResultDto,
  mapUnifiedAccountsResponseDto,
  reportQueryRequestToDto,
  syncSubmitRequestToDto,
  type AccountDetailResponseDto,
  type AccountsListResponseDto,
  type AdAccountDetail,
  type AdAccountPatchRequest,
  type AdAccountsOverview,
  type AdAccountSummary,
  type AdsPlatform,
  type BulkUnlinkRequest,
  type BulkUnlinkResponse,
  type BulkUnlinkResponseDto,
  type CancelSyncResponse,
  type CancelSyncResponseDto,
  type CreateAssetImportsRequest,
  type CreateAssetImportsResponse,
  type DiscoveryAccountsQuery,
  type DiscoveryAccountsResult,
  type DiscoveryAccountsResultDto,
  type GaUpload,
  type GaUploadDto,
  type PostImportsResponseDto,
  type LinkAccountsRequest,
  type LinkAccountsResponse,
  type LinkAccountsResponseDto,
  type AdsOAuthIdentity,
  type OAuthIdentitiesResultDto,
  type OAuthLinkRequest,
  type OAuthLinkResult,
  type OAuthLinkResultDto,
  type AdAssetImportAccepted,
  type AdAssetImportAcceptedDto,
  type AdAssetImportJob,
  type AdAccountAssetsResponseDto,
  type AdAssetImportJobDto,
  type AdAccountAssetsResult,
  type AdAssetImportRequest,
  type AdAssetServedFilter,
  type AeiQuery,
  type GroupAeiReport,
  type GroupAeiReportDto,
  type ReportQueryRequest,
  type ReportQueryResponse,
  type ReportQueryResponseDto,
  type SyncJobDetail,
  type SyncJobDetailResponseDto,
  type SyncJobsListRequest,
  type SyncJobsListResponse,
  type SyncJobsListResponseDto,
  type SyncSubmitRequest,
  type SyncSubmitResult,
  type SyncSubmitResultDto,
  type UnifiedAccountsResponseDto,
  UnifiedReportQueryRequest,
  unifiedReportQueryRequestToDto,
  assetReportsQueryRequestToDto,
  mapAssetReportsQueryResponseDto,
  type AssetReportsQueryRequest,
  type AssetReportsQueryResponse,
  type AssetReportsQueryResponseDto,
  mapMetaAssetThumbnailDto,
  type MetaAssetThumbnail,
  type MetaAssetThumbnailsResultDto,
} from '@/types';

import type { AdsPlatformService, AdsService } from '../adsService';

import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`AdsService`(R42.x 後 12 endpoint)
 * -----------------------------------------------------------------------------
 * 對齊 docs/31 §3.3 + docs/29 §5(R42.x backend MR !26 pivot)。
 *
 * **Auth**:全部走 `httpRequestWithRefresh`(cookie + CSRF;R45 起無 refresh chain);
 * Ads endpoints **全部走 CSRF 無 exempt**(對齊 docs/31 §6.4)。
 *
 * **R42.12 後的設計現況**:
 * - `linkGoogleAds`(`POST /oauth/link` 主入口,同步回 DISCOVERED state)
 * - `linkAccounts`(`POST /accounts` 取代 R42.1 的 `finalize`)+
 *   `unlinkAccounts`(`DELETE /accounts` 批次解除)
 *
 * **OAuth callback `/hai-creative/api/ads/google/oauth/callback`** 由 Google →
 * backend host **直連**跳(不經 vite proxy / nginx);FE 不打,不在此 service 暴露。
 *
 * @see docs/31 §3.1 endpoint 一覽表
 */

/**
 * R299 — Stage 1 端點依平台組路徑(Google / Meta **同名同形**,只差 `/api/ads/{platform}/`
 * 這一段;BE docs/109 §2)。`platform` 是封閉 union,不需要 encode。
 */
function stage1Paths(platform: AdsPlatform) {
  const base = `/api/ads/${platform}`;
  return {
    // OAuth Link(R42.1 主)
    oauthLink: `${base}/oauth/link`,
    // OAuth 授權身分(R290 多 Google 身分綁定;BE docs/105)
    oauthIdentities: `${base}/oauth/identities`,
    oauthIdentity: (oauthUserId: string) =>
      `${base}/oauth/identities/${encodeURIComponent(oauthUserId)}`,
    // Discovery 快照分頁/搜尋(R237 規模化 — picker 首屏/翻頁/搜尋一律走這條)
    discoveryAccounts: (discoveryId: string) =>
      `${base}/discoveries/${encodeURIComponent(discoveryId)}/accounts`,
    // Accounts(R42.12 — POST 取代 finalize / DELETE 新加批次解除 / 既有單筆 GET/PATCH/DELETE)
    accountsCollection: `${base}/accounts`,
    account: (aid: string) => `${base}/accounts/${encodeURIComponent(aid)}`,
    // Sync(R301 — Meta 四端點與 Google 同名同形,docs/111 §1;Google 路徑與 R300 之前逐位元相同)
    sync: `${base}/sync`,
    syncJob: (jid: string) => `${base}/sync/jobs/${encodeURIComponent(jid)}`,
    syncJobCancel: (jid: string) => `${base}/sync/jobs/${encodeURIComponent(jid)}/cancel`,
    syncJobsList: `${base}/sync/jobs`,
  } as const;
}

const PATH = {
  // R299 — 跨平台帳戶總覽(provider-neutral;docs/109 §6)
  accountsOverview: '/api/ads/accounts',
  // Reports
  reportsQuery: '/api/ads/google/reports/query',
  // R302 — Google + Meta 統一成效查詢(docs/113)
  reportsQueryUnified: '/api/ads/reports/query',
  // R410 — 素材成效跨媒體合併報表(docs/135):provider-neutral、只讀 BE 快查表
  reportsAssetsQueryUnified: '/api/ads/reports/assets/query',
  reportsAei: '/api/ads/google/reports/aei',
  // M7(BE docs/116)— Meta 專屬報表面。骨架與 Google 面「完全同構」,所以型別共用;
  // ⚠️ **但值域不同**:breakdown 九維 / ad 層 rankings / Meta 版素材成效只有這一面有,
  // 而統一面(`reportsQueryUnified`)只回兩平台共同集的總計列 —— 查不到那三類。
  metaReportsQuery: '/api/ads/meta/reports/query',
  // R325 — 跨來源素材分群(docs/117):**provider-neutral**(對齊 `accountsOverview` /
  // `reportsQueryUnified` 的「不分平台」慣例),所以不在 /ads/meta/* 底下。
  assetGroups: '/api/ads/assets/groups',
  // T7(docs/53)— 推素材上 GA 素材庫(assets imports;**素材庫 → Google**)
  assetImports: '/api/ads/google/assets/imports',
  assetImport: (libId: string, itemId: string, uploadId: string) =>
    `/api/ads/google/assets/imports/${encodeURIComponent(libId)}/${encodeURIComponent(itemId)}/${encodeURIComponent(uploadId)}`,
  // M5(BE docs/118)— 推素材上 **Meta** 素材庫(**素材庫 / 畫布產物 → Meta**)。
  // ⚠️ GET 是**一段** `{upload_id}`(Google 那支是 lib/item/upload 三段)——
  // 逐欄鏡射到這裡就分岔了,照抄 Google 的組法會 404。
  metaAssetThumbnailUrls: '/api/ads/meta/assets/thumbnail-urls',
  metaAssetUploads: '/api/ads/meta/assets/uploads',
  metaAssetUpload: (uploadId: string) =>
    `/api/ads/meta/assets/uploads/${encodeURIComponent(uploadId)}`,
  // I1(docs/89 §3b)— 廣告帳戶素材匯進素材庫(**Google → 素材庫**;單數 `import`,
  // 與上面 T7 的複數 `imports` 只差一個 s 而方向相反 —— 別接錯邊)
  // R390 — 帳戶素材清單(docs/114 §9.2;⚠️ 與下面 `/assets/import` 差一段路徑,方向也不同:
  // 這支是「列出帳戶上有什麼」,import 那支是「把選中的搬進素材庫」)
  adAccountAssets: '/api/ads/google/assets',
  adAssetImport: '/api/ads/google/assets/import',
  // R308(BE docs/36 B7)— 重探寫入能力;**只有 Google 面**(Meta 的 aid 回 404)
  capabilityReprobe: (aid: string) =>
    `/api/ads/google/accounts/${encodeURIComponent(aid)}/capability/reprobe`,
  adAssetImportJob: (jobId: string) =>
    `/api/ads/google/assets/import/${encodeURIComponent(jobId)}`,
} as const;

/**
 * T9(docs/51)— ads group-scoped 端點需帶 `X-Current-Group`,但 `/api/ads` 在
 * `GROUP_HEADER_EXEMPT_PATTERNS` 內(R47 短期 opt-out),auto 注入被 skip。此處**明確**
 * 從 groupContext 補 header(extraHeaders 在 auto 注入後 merge,exempt path 也生效),
 * 不動 global exemption / R42 OAuth flow。gid 缺時交 BE L1.5 回 400 CURRENT_GROUP_REQUIRED。
 */
function currentGroupHeader(config: HttpClientConfig): Record<string, string> | undefined {
  const gid = config.getCurrentGroupId?.();
  return gid ? { 'X-Current-Group': gid } : undefined;
}

/**
 * R237 — discovery accounts 分頁/搜尋 query string(docs/31 §3.3b 五參數)。
 *
 * 全部選填,只送有值的(BE 端 `q` 空/缺 = 瀏覽模式;`limit` 缺 = BE 預設 50)。
 * `q` **不清洗**直接送 —— BE 自己去連字號/空白比 customer_id 前綴(Google Ads UI
 * 的 `123-456-7890` 格式整段貼上可用),FE 先清洗 = 兩套語意。
 */
function buildDiscoveryAccountsQuery(query?: DiscoveryAccountsQuery): string {
  if (!query) return '';
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.status) params.set('status', query.status);
  if (query.selectableOnly) params.set('selectable_only', 'true');
  if (query.topLevelOnly) params.set('top_level_only', 'true');
  if (query.parentCustomerId) params.set('parent_customer_id', query.parentCustomerId);
  if (query.offset !== undefined && query.offset > 0) params.set('offset', String(query.offset));
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * R299 — `GET /api/ads/accounts` 的 `?platform=` query:可重複帶(BE `list[PlatformTag]`
 * 去重保序);省略 = 全部。空陣列也視同省略(送空 query 沒有意義)。
 */
function buildAccountsOverviewQuery(platforms?: readonly AdsPlatform[]): string {
  if (!platforms || platforms.length === 0) return '';
  const params = new URLSearchParams();
  for (const p of platforms) params.append('platform', p);
  return `?${params.toString()}`;
}

function buildSyncJobsListQuery(req?: SyncJobsListRequest): string {
  if (!req) return '';
  const params = new URLSearchParams();
  if (req.adAccountId) params.set('ad_account_id', req.adAccountId);
  if (req.status) params.set('status', req.status);
  if (req.limit !== undefined) params.set('limit', String(req.limit));
  if (req.cursor) params.set('cursor', req.cursor);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * R299 — 某一個平台的 Stage 1 端點組(Google / Meta 同形)。所有 Google 既有方法都是
 * `createPlatformService(config, 'google')` 的轉發,行為(路徑 / body / header)與 R298
 * 之前逐位元相同。
 */
function createPlatformService(config: HttpClientConfig, platform: AdsPlatform): AdsPlatformService {
  const P = stage1Paths(platform);
  return {
    platform,

    // === Stage 1 — OAuth Link(R42.1 主)===

    async link(req?: OAuthLinkRequest): Promise<OAuthLinkResult> {
      // R290 — 沒指定身分時 `oauthLinkRequestToDto` 回 undefined = **不帶 body**,
      // request 與 R288 之前逐位元相同(transport 層 body===undefined 就不設
      // Content-Type、不 stringify)。指定了才送 `{oauth_user_id}`。
      const env = await httpRequestWithRefresh<OAuthLinkResultDto>(
        config,
        'POST',
        P.oauthLink,
        oauthLinkRequestToDto(req),
      );
      return mapOAuthLinkResultDto(unwrapResult(env, `ads.${platform}.link`));
    },

    // === R290 — OAuth 授權身分(多身分綁定;BE docs/105 / docs/109)===

    async listOAuthIdentities(): Promise<AdsOAuthIdentity[]> {
      const env = await httpRequestWithRefresh<OAuthIdentitiesResultDto>(
        config,
        'GET',
        P.oauthIdentities,
      );
      return mapOAuthIdentitiesResultDto(unwrapResult(env, `ads.${platform}.listOAuthIdentities`));
    },

    async unlinkOAuthIdentity(oauthUserId: string, opts?: { force?: boolean }): Promise<void> {
      // `?force=true` 只在明確要求時帶(不帶 = BE 預檢閘,名下有帳戶就回 409)。
      const qs = opts?.force ? '?force=true' : '';
      await httpRequestWithRefresh(config, 'DELETE', `${P.oauthIdentity(oauthUserId)}${qs}`);
    },

    async listDiscoveryAccounts(
      discoveryId: string,
      query?: DiscoveryAccountsQuery,
    ): Promise<DiscoveryAccountsResult> {
      // R237 — BE `require_current_group()`:header 缺會 fallback 到 me doc 的
      // current_group_id,但切工作區的瞬間 me doc 可能落後 FE,顯式帶(對齊 sync /
      // reports 慣例)才不會把 A 工作區的 discovery 讀權判給 B。
      const env = await httpRequestWithRefresh<DiscoveryAccountsResultDto>(
        config,
        'GET',
        `${P.discoveryAccounts(discoveryId)}${buildDiscoveryAccountsQuery(query)}`,
        undefined,
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapDiscoveryAccountsResultDto(
        unwrapResult(env, `ads.${platform}.listDiscoveryAccounts`),
      );
    },

    // === Stage 1 — Link / Unlink accounts(R42.12 backend pivot)===

    async linkAccounts(req: LinkAccountsRequest): Promise<LinkAccountsResponse> {
      const env = await httpRequestWithRefresh<LinkAccountsResponseDto>(
        config,
        'POST',
        P.accountsCollection,
        linkAccountsRequestToDto(req),
      );
      return mapLinkAccountsResponseDto(unwrapResult(env, `ads.${platform}.linkAccounts`));
    },

    async unlinkAccounts(req: BulkUnlinkRequest): Promise<BulkUnlinkResponse> {
      const env = await httpRequestWithRefresh<BulkUnlinkResponseDto>(
        config,
        'DELETE',
        P.accountsCollection,
        bulkUnlinkRequestToDto(req),
      );
      return mapBulkUnlinkResponseDto(unwrapResult(env, `ads.${platform}.unlinkAccounts`));
    },

    // === Stage 1 — Ad accounts ===

    async listAccounts(): Promise<AdAccountSummary[]> {
      const env = await httpRequestWithRefresh<AccountsListResponseDto>(
        config,
        'GET',
        P.accountsCollection,
      );
      // 單平台端點知道自己是誰:wire 缺 `platform`(BE 2026-08-31 前的 build)以它補
      return mapAccountsListResponseDto(
        unwrapResult(env, `ads.${platform}.listAccounts`),
        platform,
      );
    },

    async getAccount(adAccountId: string): Promise<AdAccountDetail> {
      // R42.12 — BE response envelope `{ ad_account: AdAccountOut }`(spec §4.2)
      const env = await httpRequestWithRefresh<AccountDetailResponseDto>(
        config,
        'GET',
        P.account(adAccountId),
      );
      return mapAccountDetailResponseDto(
        unwrapResult(env, `ads.${platform}.getAccount`),
        platform,
      );
    },

    async patchAccount(adAccountId: string, req: AdAccountPatchRequest): Promise<AdAccountDetail> {
      const env = await httpRequestWithRefresh<AccountDetailResponseDto>(
        config,
        'PATCH',
        P.account(adAccountId),
        accountPatchRequestToDto(req),
      );
      return mapAccountDetailResponseDto(
        unwrapResult(env, `ads.${platform}.patchAccount`),
        platform,
      );
    },

    async deleteAccount(adAccountId: string): Promise<void> {
      await httpRequestWithRefresh(config, 'DELETE', P.account(adAccountId));
    },

    // === R301 — Sync 四端點(BE docs/111;Google / Meta 同名同形)===

    async submitSync(req: SyncSubmitRequest): Promise<SyncSubmitResult> {
      const env = await httpRequestWithRefresh<SyncSubmitResultDto>(
        config,
        'POST',
        P.sync,
        syncSubmitRequestToDto(req),
      );
      return mapSyncSubmitResultDto(unwrapResult(env, 'ads.submitSync'));
    },

    async getSyncJob(syncJobId: string): Promise<SyncJobDetail> {
      // R44.1 — BE envelope `{ sync_job: SyncJobOut }`(docs/36 §3),需 unwrap
      const env = await httpRequestWithRefresh<SyncJobDetailResponseDto>(
        config,
        'GET',
        P.syncJob(syncJobId),
      );
      return mapSyncJobDetailResponseDto(unwrapResult(env, 'ads.getSyncJob'));
    },

    async cancelSync(syncJobId: string): Promise<CancelSyncResponse> {
      // R44.1 — BE 真實回 `{ sync_job_id, status }`(docs/36 §4);idempotent
      const env = await httpRequestWithRefresh<CancelSyncResponseDto>(
        config,
        'POST',
        P.syncJobCancel(syncJobId),
      );
      return mapCancelSyncResponseDto(unwrapResult(env, 'ads.cancelSync'));
    },

    async listSyncJobs(req?: SyncJobsListRequest): Promise<SyncJobsListResponse> {
      const env = await httpRequestWithRefresh<SyncJobsListResponseDto>(
        config,
        'GET',
        `${P.syncJobsList}${buildSyncJobsListQuery(req)}`,
      );
      return mapSyncJobsListResponseDto(unwrapResult(env, 'ads.listSyncJobs'));
    },
  };
}

export function createHttpAdsService(config: HttpClientConfig): AdsService {
  // 兩個平台各一份(建構便宜:只是路徑字串);Google 那份同時是下面既有方法的實作。
  const byPlatform: Record<AdsPlatform, AdsPlatformService> = {
    google: createPlatformService(config, 'google'),
    meta: createPlatformService(config, 'meta'),
  };
  const google = byPlatform.google;

  return {
    // === R299 — 平台分流 ===

    forPlatform(platform: AdsPlatform): AdsPlatformService {
      return byPlatform[platform];
    },

    async listAccountsOverview(platforms?: readonly AdsPlatform[]): Promise<AdAccountsOverview> {
      const env = await httpRequestWithRefresh<UnifiedAccountsResponseDto>(
        config,
        'GET',
        `${PATH.accountsOverview}${buildAccountsOverviewQuery(platforms)}`,
      );
      return mapUnifiedAccountsResponseDto(unwrapResult(env, 'ads.listAccountsOverview'));
    },

    // === Stage 1 — Google 別名(行為 = `forPlatform('google')`,既有 caller 零改)===

    linkGoogleAds: (req) => google.link(req),
    listOAuthIdentities: () => google.listOAuthIdentities(),
    unlinkOAuthIdentity: (id, opts) => google.unlinkOAuthIdentity(id, opts),
    listDiscoveryAccounts: (discoveryId, query) => google.listDiscoveryAccounts(discoveryId, query),
    linkAccounts: (req) => google.linkAccounts(req),
    unlinkAccounts: (req) => google.unlinkAccounts(req),
    listAccounts: () => google.listAccounts(),
    getAccount: (aid) => google.getAccount(aid),
    patchAccount: (aid, req) => google.patchAccount(aid, req),
    deleteAccount: (aid) => google.deleteAccount(aid),

    // === Stage 1 — Sync(Google 別名;R301 起本體在 `createPlatformService`)===

    submitSync: (req) => google.submitSync(req),
    getSyncJob: (jid) => google.getSyncJob(jid),
    cancelSync: (jid) => google.cancelSync(jid),
    listSyncJobs: (req) => google.listSyncJobs(req),

    // === Stage 1 — Reports ===

    async queryReports(req: ReportQueryRequest): Promise<ReportQueryResponse> {
      const env = await httpRequestWithRefresh<ReportQueryResponseDto>(
        config,
        'POST',
        PATH.reportsQuery,
        reportQueryRequestToDto(req),
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapReportQueryResponseDto(unwrapResult(env, 'ads.queryReports'));
    },

    async queryMetaReports(req: ReportQueryRequest): Promise<ReportQueryResponse> {
      const env = await httpRequestWithRefresh<ReportQueryResponseDto>(
        config,
        'POST',
        PATH.metaReportsQuery,
        reportQueryRequestToDto(req),
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapReportQueryResponseDto(unwrapResult(env, 'ads.queryMetaReports'));
    },

    async queryAssetGroups(req: AssetGroupsQuery): Promise<AssetGroupsResult> {
      const env = await httpRequestWithRefresh<AssetGroupsResponseDto>(
        config,
        'POST',
        PATH.assetGroups,
        assetGroupsQueryToDto(req),
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapAssetGroupsResponseDto(unwrapResult(env, 'ads.queryAssetGroups'));
    },

    async queryUnifiedReports(req: UnifiedReportQueryRequest): Promise<ReportQueryResponse> {
      const env = await httpRequestWithRefresh<ReportQueryResponseDto>(
        config,
        'POST',
        PATH.reportsQueryUnified,
        unifiedReportQueryRequestToDto(req),
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapReportQueryResponseDto(unwrapResult(env, 'ads.queryUnifiedReports'));
    },

    async queryAssetReports(req: AssetReportsQueryRequest): Promise<AssetReportsQueryResponse> {
      // 守門同其他報表面:登入(cookie)+ `X-Current-Group`;POST 的 CSRF 由 transport 自動帶
      const env = await httpRequestWithRefresh<AssetReportsQueryResponseDto>(
        config,
        'POST',
        PATH.reportsAssetsQueryUnified,
        assetReportsQueryRequestToDto(req),
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapAssetReportsQueryResponseDto(unwrapResult(env, 'ads.queryAssetReports'));
    },

    async getAei(query?: AeiQuery): Promise<GroupAeiReport> {
      // from/to 同給或同省(BE:只給一邊 → 422);省略 → BE 回最近 8 完整週。
      // `asset_source` / `asset_link_scope`(docs/89 §2):**不帶 = BE 預設 platform
      // closed-loop 面,結果恆空** —— 素材成效表一定要帶 `assetSource: 'google'`。
      const qs = new URLSearchParams();
      if (query?.range) {
        qs.set('from', query.range.from);
        qs.set('to', query.range.to);
      }
      if (query?.assetSource) qs.set('asset_source', query.assetSource);
      if (query?.assetLinkScope) qs.set('asset_link_scope', query.assetLinkScope);
      const search = qs.toString();
      const path = search ? `${PATH.reportsAei}?${search}` : PATH.reportsAei;
      const env = await httpRequestWithRefresh<GroupAeiReportDto>(
        config,
        'GET',
        path,
        undefined,
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapGroupAeiReportDto(unwrapResult(env, 'ads.getAei'));
    },

    async getMetaAssetThumbnailUrls(
      customerId: string,
      assetIds: readonly string[],
    ): Promise<MetaAssetThumbnail[]> {
      // 空批次不打 —— BE 對空 `asset_ids` 回 422,而那是呼叫端的防呆訊號不是使用者的錯
      if (assetIds.length === 0) return [];
      const qs = new URLSearchParams();
      qs.set('customer_id', customerId);
      // ⚠️ wire 是**逗號串**不是重複 query param(BE docs/114 §8:`asset_ids=a,b,c`)
      qs.set('asset_ids', assetIds.join(','));
      const env = await httpRequestWithRefresh<MetaAssetThumbnailsResultDto>(
        config,
        'GET',
        `${PATH.metaAssetThumbnailUrls}?${qs.toString()}`,
        undefined,
        { extraHeaders: currentGroupHeader(config) },
      );
      const res = unwrapResult(env, 'ads.getMetaAssetThumbnailUrls');
      return (res.items ?? []).map(mapMetaAssetThumbnailDto);
    },

    // === T7(docs/53)— 推素材上 GA 素材庫(assets imports)===
    // 三個端點皆 group-scoped;`/api/ads` 在 GROUP_HEADER_EXEMPT 內,故明確補
    // `X-Current-Group`(對齊 queryReports / getAei)。POST 為寫操作,CSRF 由
    // httpRequestWithRefresh 自動帶。

    async importAssets(req: CreateAssetImportsRequest): Promise<CreateAssetImportsResponse> {
      const env = await httpRequestWithRefresh<PostImportsResponseDto>(
        config,
        'POST',
        PATH.assetImports,
        createAssetImportsRequestToDto(req),
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapPostImportsResponseDto(unwrapResult(env, 'ads.importAssets'));
    },


    async getAssetImport(libId: string, itemId: string, uploadId: string): Promise<GaUpload> {
      // BE envelope `{ import: GaUploadOut }`(docs/53 §A.3);需 unwrap `.import`
      const env = await httpRequestWithRefresh<{ import: GaUploadDto }>(
        config,
        'GET',
        PATH.assetImport(libId, itemId, uploadId),
        undefined,
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapGaUploadEntityDto(unwrapResult(env, 'ads.getAssetImport').import);
    },

    async uploadMetaAssets(
      req: CreateMetaAssetUploadsRequest,
    ): Promise<CreateMetaAssetUploadsResponse> {
      // ⚠️ body 走 `extra=forbid`:只送契約上的欄。來源**恰一組**,混填 → 422
      // (Google T7 會靜默忽略多的欄,Meta 不會)。分批與去重是呼叫端的責任
      // (`features/ads/metaUpload.ts`),service 層不代勞 —— 代勞的話呼叫端會以為
      // 送了 80 筆就真的送了 80 筆。
      const env = await httpRequestWithRefresh<CreateMetaAssetUploadsResponseDto>(
        config,
        'POST',
        PATH.metaAssetUploads,
        {
          customer_id: req.customerId,
          items: req.items.map((it) =>
            'libId' in it && it.libId !== undefined
              ? { lib_id: it.libId, item_id: it.itemId }
              : { project_id: it.projectId, job_id: it.jobId, asset_id: it.assetId },
          ),
          // R407 — 同 Google:只有 `true` 才上 wire(`extra="forbid"`,舊 BE 見鍵即 422)
          ...(req.force === true ? { force: true } : {}),
        },
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapCreateMetaAssetUploadsResponseDto(unwrapResult(env, 'ads.uploadMetaAssets'));
    },

    async getMetaAssetUpload(uploadId: string): Promise<MetaAssetUpload> {
      const env = await httpRequestWithRefresh<GetMetaAssetUploadResponseDto>(
        config,
        'GET',
        PATH.metaAssetUpload(uploadId),
        undefined,
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapMetaAssetUploadDto(unwrapResult(env, 'ads.getMetaAssetUpload').upload);
    },

    // === I1(docs/89 §3b)— 廣告帳戶素材 → 我們的素材庫(方向與上面 T7 相反)===

    async listAdAccountAssets(
      customerId: string,
      opts: { served: AdAssetServedFilter; kind?: 'IMAGE' | 'VIDEO'; limit?: number },
    ): Promise<AdAccountAssetsResult> {
      const qs = new URLSearchParams();
      qs.set('customer_id', customerId);
      // `served` 一律顯式帶:呼叫端的語意就是「哪一個 tab」,靠 BE 預設(all)等於
      // 把 tab 語意藏進缺參數裡 —— 存量清理後 all ≈ served,讀的人會誤判。
      qs.set('served', opts.served);
      if (opts.kind) qs.set('kind', opts.kind);
      if (opts.limit != null) qs.set('limit', String(opts.limit));
      const env = await httpRequestWithRefresh<AdAccountAssetsResponseDto>(
        config,
        'GET',
        `${PATH.adAccountAssets}?${qs.toString()}`,
        undefined,
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapAdAccountAssetsResponseDto(unwrapResult(env, 'ads.listAdAccountAssets'));
    },

    async importAdAssets(req: AdAssetImportRequest): Promise<AdAssetImportAccepted> {
      const env = await httpRequestWithRefresh<AdAssetImportAcceptedDto>(
        config,
        'POST',
        PATH.adAssetImport,
        adAssetImportRequestToDto(req),
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapAdAssetImportAcceptedDto(unwrapResult(env, 'ads.importAdAssets'));
    },

    async reprobeAccountCapability(adAccountId: string): Promise<AccountCapability> {
      const env = await httpRequestWithRefresh<AccountCapabilityDto>(
        config,
        'POST',
        PATH.capabilityReprobe(adAccountId),
        undefined,
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapAccountCapabilityDto(unwrapResult(env, 'ads.reprobeAccountCapability'));
    },

    async getAdAssetImportJob(jobId: string): Promise<AdAssetImportJob> {
      const env = await httpRequestWithRefresh<AdAssetImportJobDto>(
        config,
        'GET',
        PATH.adAssetImportJob(jobId),
        undefined,
        { extraHeaders: currentGroupHeader(config) },
      );
      return mapAdAssetImportJobDto(unwrapResult(env, 'ads.getAdAssetImportJob'));
    },
  };
}
